import { useMutation } from "@apollo/client/react";
import { DSLinkButton } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms/DSModal";
import { faHistory } from "@fortawesome/free-solid-svg-icons";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React, { useEffect, useEffectEvent, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { defaultErrorHandler } from "../../../utils/operationTracking";
import { alert } from "../../common/Alert";
import {
  CREATE_PATIENT_MUTATION,
  EDIT_PATIENT_MUTATION,
  DELETE_PATIENT_MUTATION,
} from "../api/mutations";
import { getSpeciesLabel, species } from "../utils";
import CreatePatientForm from "./PatientForm";
import { getDateFromInput } from "../../common/date/utils";

export function EditablePatientModal(props) {
  const {
    currentPatientInfo,
    shouldShow,
    hideModal,
    redirectToNotebook,
    isForPatientEdit,
    refetch,
    isForPatientSelectionAction,
    setSelectedPatientName,
    setSelectedPatientUuid,
    onAddPatient,
  } = props;

  const methods = useForm();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = methods;

  const navigate = useNavigate();
  const location = useLocation();

  const isForPatientNoteCreation =
    location.pathname === "/new_note/select";

  const [submitClicked, setSubmitClicked] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [isAgeInputTypeDOB, setIsAgeInputTypeDOB] = useState(true);

  const [sex, setSex] = useState(null);
  const [intact, setIntact] = useState(true);
  const [
    shouldShowDeletePatientModal,
    setShouldShowDeletePatientModal,
  ] = useState(false);

  const [createPatient] = useMutation(CREATE_PATIENT_MUTATION);
  const [editPatient] = useMutation(EDIT_PATIENT_MUTATION);
  const [deletePatient] = useMutation(DELETE_PATIENT_MUTATION);

  const speciesItems = Object.keys(species).map((item) => {
    return {
      label: getSpeciesLabel(
        species[item].displayName,
        species[item].icon,
      ),
      name: item,
    };
  });

  const syncPatientFormFromCurrent = useEffectEvent(() => {
    if (!currentPatientInfo) return;
    const {
      name,
      lastNameOfOwner,
      breed,
      birthdayParts: { dd, mm, yyyy },
      isIntact,
      sex,
      species,
      color,
      microchip,
      ownerContactInfo,
      emergencyContactInfo,
    } = currentPatientInfo;

    const speciesFromList = speciesItems.find(
      ({ name }) => name === species,
    );

    setSelectedSpecies(speciesFromList);
    setSex(sex);
    setIntact(isIntact);
    setIsAgeInputTypeDOB(true);
    reset({
      patientName: name,
      ownersLastName: lastNameOfOwner,
      breed,
      dd,
      mm,
      yyyy,
      weeks: null,
      months: null,
      years: null,
      color,
      microchip,
      ownerContactInfo,
      emergencyContactInfo,
    });
  });

  useEffect(() => {
    if (isForPatientEdit && currentPatientInfo) {
      syncPatientFormFromCurrent();
    }
  }, [currentPatientInfo, isForPatientEdit]);

  const onError = () => {
    alert(
      "error",
      "Patient form errors, please review above for details.",
    );
  };

  function onSubmit(data) {
    setSubmitClicked(true);

    if (sex === null) {
      alert("error", "Please specify the patient's sex!");
      setSubmitClicked(false);
      return;
    }

    let {
      patientName,
      ownersLastName,
      breed,
      weeks,
      months,
      years,
      yyyy,
      mm,
      dd,
      color,
      microchip,
      ownerContactInfo,
      emergencyContactInfo,
    } = data;

    const birthday = isAgeInputTypeDOB
      ? getDateFromInput({ yyyy, mm, dd })
      : null;

    // Refactor note, move to api.js
    if (isForPatientEdit) {
      editPatient({
        variables: {
          patientUuid: currentPatientInfo?.uuid,
          patientInput: {
            name: patientName,
            lastNameOfOwner: ownersLastName ?? "",
            species: selectedSpecies?.name ?? "",
            breed: breed ?? "",
            weeks: weeks ?? 0,
            months: months ?? 0,
            years: years ?? 0,
            birthday: birthday ?? null,
            sex: sex,
            isIntact: intact,
            color: color ?? null,
            microchip: microchip ?? null,
            ownerContactInfo: ownerContactInfo ?? null,
            emergencyContactInfo: emergencyContactInfo ?? null,
          },
        },
      })
        .then(() => {
          hideModal();
          alert("success", "Patient updated successfully!");
        })
        .catch(defaultErrorHandler)
        .finally(() => {
          setSubmitClicked(false);
        });
      refetch();
    } else {
      mixpanel.track("Create Patient");
      createPatient({
        variables: {
          name: patientName,
          lastNameOfOwner: ownersLastName ?? "",
          species: selectedSpecies?.name ?? "",
          breed: breed ?? "",
          weeks: weeks ?? 0,
          months: months ?? 0,
          years: years ?? 0,
          birthday: birthday ?? null,
          sex: sex,
          isIntact: intact,
          createdAt: moment().format(),
          color: color ?? null,
          microchip: microchip ?? null,
          ownerContactInfo: ownerContactInfo ?? null,
          emergencyContactInfo: emergencyContactInfo ?? null,
        },
      })
        .then((response) => {
          const { uuid } = response.data.createPatient.patient;

          if (
            !isForPatientSelectionAction &&
            !isForPatientNoteCreation
          ) {
            redirectToNotebook(uuid);
            alert(
              "info",
              <h5 className="text-sm font-normal text-white">
                New patient {patientName} created!{" "}
                <a
                  onClick={() => {
                    navigate("/notebook", {
                      state: { shouldShow: true },
                    });
                  }}
                  className="underline hover:text-indigo-400 transition-all cursor-pointer"
                >
                  Click here
                </a>{" "}
                to create another one.
              </h5>,
            );
          }
          if (isForPatientSelectionAction) {
            hideModal();
            setSelectedPatientName(patientName);
            setSelectedPatientUuid(uuid);
            alert("success", `New patient ${patientName} created!`);
          }
          if (isForPatientNoteCreation) {
            hideModal();
            onAddPatient(uuid);
            alert("success", `New patient ${patientName} created!`);
          }
        })
        .catch(defaultErrorHandler)
        .finally(() => {
          setSubmitClicked(false);
        });
    }
  }

  return (
    <>
      <DSModal
        isOpen={shouldShow}
        onClose={hideModal}
        title={isForPatientEdit ? "Edit Patient" : "New Patient"}
        dataTestId="editPatientModal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => hideModal(),
            dataTestId: "cancel-button",
          },
          {
            label: "Confirm",
            variant: "primary",
            onClick: handleSubmit(onSubmit, onError),
            disabled: submitClicked,
            dataTestId: "confirmPatient",
          },
        ]}
      >
        {isForPatientEdit && (
          <div className="flex flex-row items-center justify-between">
            <DSLinkButton
              icon={faHistory}
              onClick={() => {
                navigate(
                  `/audit_trail/patient/${currentPatientInfo?.uuid}`,
                );
              }}
              dataTestId="pastRevisionsButton"
            >
              Past Revisions
            </DSLinkButton>
            <DSLinkButton
              variant="danger"
              onClick={() => setShouldShowDeletePatientModal(true)}
              dataTestId="deletePatientButton"
            >
              Delete Patient
            </DSLinkButton>
          </div>
        )}
        <FormProvider {...methods}>
          <CreatePatientForm
            control={control}
            errors={errors}
            sex={sex}
            setSex={setSex}
            intact={intact}
            setIntact={setIntact}
            selectedSpecies={selectedSpecies}
            setSelectedSpecies={setSelectedSpecies}
            isAgeInputTypeDOB={isAgeInputTypeDOB}
            setIsAgeInputTypeDOB={setIsAgeInputTypeDOB}
            submitClicked={submitClicked}
            speciesItems={speciesItems}
          />
        </FormProvider>
      </DSModal>
      <DSModal
        isOpen={shouldShowDeletePatientModal}
        onClose={() => setShouldShowDeletePatientModal(false)}
        title="Delete Patient"
        dataTestId="Yes, Delete-modal"
        actions={[
          {
            label: "Never Mind",
            variant: "cancel",
            onClick: () => setShouldShowDeletePatientModal(false),
            dataTestId: "cancel-button",
          },
          {
            label: "Yes, Delete",
            variant: "primary",
            onClick: () => {
              deletePatient({
                variables: {
                  patientUuid: currentPatientInfo?.uuid,
                },
              })
                .then(() => {
                  navigate("/notebook");
                  alert("success", "Patient deleted successfully.");
                })
                .catch(defaultErrorHandler);
            },
            dataTestId: "confirm-button",
          },
        ]}
      >
        <h5>
          Are you sure you&apos;d like to{" "}
          <strong className="text-red-500">
            delete this patient
          </strong>
          ? All of this patients&apos; existing{" "}
          <strong>notes and recordings</strong> will be deleted as
          well. This action cannot be undone.
        </h5>
      </DSModal>
    </>
  );
}
